import type { FC, ReactNode } from 'react'
import type { Category, StoreSettings } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import {
    ChevronDown,
    Menu,
    Search,
    ShoppingBag,
    Sparkles,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Drawer } from '@/components/generals/dialog'
import { buttonStyles } from '@/components/generals/button'

import {
    LOGO_HEIGHT,
    LOGO_SRC,
    LOGO_WIDTH
} from '@/constants/site'

import { useCart } from './cart-context'
import { CategoryIcon } from './category-icon'

interface StoreHeaderProps {
    settings: StoreSettings
    categories: Array<Category>
}

const NAV_LINKS = [
    { href: '/', label: 'Beranda' },
    { href: '/produk', label: 'Produk' },
    { href: '/#promo', label: 'Promo' },
    { href: '/produk?kategori=paket-hemat', label: 'Paket Hemat' },
    { href: '/#kontak', label: 'Kontak' }
]

export const StoreHeader: FC<StoreHeaderProps> = ({ settings, categories }): ReactNode => {
    const router = useRouter()
    const cart = useCart()
    const [menuOpen, setMenuOpen] = useState(false)
    const [categoriesOpen, setCategoriesOpen] = useState(false)
    const [bump, setBump] = useState(false)
    const categoryRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!cart.lastAdded) return

        setBump(true)

        const timer = window.setTimeout(() => setBump(false), 700)

        return () => window.clearTimeout(timer)
    }, [cart.lastAdded])

    useEffect(() => {
        if (!categoriesOpen) return

        const onPointer = (event: PointerEvent) => {
            if (!categoryRef.current?.contains(event.target as Node)) setCategoriesOpen(false)
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setCategoriesOpen(false)
        }

        document.addEventListener('pointerdown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            document.removeEventListener('pointerdown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [categoriesOpen])

    useEffect(() => {
        const close = () => {
            setMenuOpen(false)
            setCategoriesOpen(false)
        }

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const isActive = (href: string): boolean => {
        if (href === '/') return router.pathname === '/'
        if (href.startsWith('/#')) return false
        if (href.includes('?')) return router.asPath === href

        return router.pathname.startsWith(href)
    }

    return (
        <>
            {settings.announcement && (
                <div className='relative z-50 bg-brand-choco text-brand-cream'>
                    <div className='mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2 text-center text-[13px] font-medium sm:text-sm'>
                        <Sparkles className='size-4 shrink-0 text-brand-yellow' aria-hidden='true' />
                        <span data-mdlfy-cms={`store_settings:${settings.id}:announcement`}>
                            {settings.announcement}
                        </span>
                    </div>
                </div>
            )}

            <header className='sticky top-0 z-50 border-b-2 border-brand-choco/10 bg-background/90 backdrop-blur-md'>
                <div className='mx-auto flex h-[84px] max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8'>
                    <Link href='/' className='group flex shrink-0 items-center gap-2.5 rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40'>
                        <Image
                            src={LOGO_SRC}
                            alt='Logo GEMAR JAJAN'
                            width={LOGO_WIDTH}
                            height={LOGO_HEIGHT}
                            priority
                            sizes='64px'
                            className='logo-blend h-[66px] w-auto transition-transform duration-300 group-hover:-rotate-3'
                        />
                        <span className='hidden leading-none sm:block lg:hidden xl:block'>
                            <span className='block font-heading text-xl font-bold tracking-wide text-brand-choco' data-mdlfy-cms={`store_settings:${settings.id}:store_name`}>
                                {settings.store_name}
                            </span>
                            {settings.tagline && (
                                <span className='mt-1 block text-xs font-medium text-muted-foreground' data-mdlfy-cms={`store_settings:${settings.id}:tagline`}>
                                    {settings.tagline}
                                </span>
                            )}
                        </span>
                    </Link>

                    <nav className='ml-2 hidden items-center gap-0.5 xl:ml-6 lg:flex' aria-label='Navigasi utama'>
                        {NAV_LINKS.slice(0, 2).map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'rounded-full px-4 py-2 font-heading text-[15px] font-semibold text-brand-choco transition-colors hover:bg-brand-sand/70',
                                    isActive(link.href) && 'bg-brand-yellow/90 hover:bg-brand-yellow'
                                )}
                                aria-current={isActive(link.href) ? 'page' : undefined}
                            >
                                {link.label}
                            </Link>
                        ))}

                        <div ref={categoryRef} className='relative'>
                            <button
                                type='button'
                                onClick={() => setCategoriesOpen(open => !open)}
                                className='inline-flex items-center gap-1 rounded-full px-4 py-2 font-heading text-[15px] font-semibold text-brand-choco transition-colors hover:bg-brand-sand/70'
                                aria-expanded={categoriesOpen}
                                aria-controls='menu-kategori'
                            >
                                Kategori
                                <ChevronDown className={cn('size-4 transition-transform', categoriesOpen && 'rotate-180')} aria-hidden='true' />
                            </button>

                            {categoriesOpen && (
                                <div
                                    id='menu-kategori'
                                    className='absolute left-0 top-full z-50 mt-3 w-[560px] animate-pop-in rounded-[28px] border-2 border-brand-choco bg-white p-3 shadow-[0_8px_0_0_#3B1F0E]'
                                >
                                    <div className='grid grid-cols-2 gap-1'>
                                        {categories.map(category => (
                                            <Link
                                                key={category.id}
                                                href={`/produk?kategori=${category.slug}`}
                                                className='group flex items-center gap-3 rounded-2xl p-2.5 transition-colors hover:bg-brand-cream'
                                            >
                                                <span
                                                    className='flex size-10 shrink-0 items-center justify-center rounded-xl border-2 border-brand-choco text-brand-choco'
                                                    style={{ backgroundColor: category.color }}
                                                >
                                                    <CategoryIcon slug={category.slug} className='size-5' />
                                                </span>
                                                <span className='min-w-0'>
                                                    <span className='block truncate font-heading text-[15px] font-semibold text-brand-choco' data-mdlfy-cms={`categories:${category.id}:name`}>
                                                        {category.name}
                                                    </span>
                                                    <span className='block text-xs text-muted-foreground'>
                                                        {category.product_count} produk
                                                    </span>
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {NAV_LINKS.slice(2).map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'rounded-full px-4 py-2 font-heading text-[15px] font-semibold text-brand-choco transition-colors hover:bg-brand-sand/70',
                                    isActive(link.href) && 'bg-brand-yellow/90 hover:bg-brand-yellow'
                                )}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='ml-auto flex items-center gap-2'>
                        <Link href='/produk?cari=' className={buttonStyles({ variant: 'ghost', size: 'icon', className: 'hidden sm:inline-flex' })} aria-label='Cari camilan'>
                            <Search className='size-5' />
                        </Link>

                        <button
                            type='button'
                            onClick={cart.openDrawer}
                            className={cn(buttonStyles({ variant: 'secondary', size: 'md', className: 'relative pl-4 pr-4 sm:pr-5' }), bump && 'animate-wiggle')}
                            aria-label={`Buka keranjang, ${cart.count} barang`}
                        >
                            <ShoppingBag className='size-5' aria-hidden='true' />
                            <span className='hidden sm:inline'>Keranjang</span>
                            {cart.ready && cart.count > 0 && (
                                <span className='absolute -right-1.5 -top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-brand-choco bg-brand-red px-1 text-xs font-bold text-white'>
                                    {cart.count > 99 ? '99+' : cart.count}
                                </span>
                            )}
                        </button>

                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            className={buttonStyles({ variant: 'ghost', size: 'icon', className: 'lg:hidden' })}
                            aria-label='Buka menu'
                            aria-expanded={menuOpen}
                        >
                            <Menu className='size-6' />
                        </button>
                    </div>
                </div>
            </header>

            <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} label='Menu navigasi'>
                <div className='flex items-center justify-between border-b-2 border-brand-choco/10 px-5 py-4'>
                    <Image src={LOGO_SRC} alt='GEMAR JAJAN' width={LOGO_WIDTH} height={LOGO_HEIGHT} sizes='48px' className='logo-blend h-14 w-auto' />
                    <button type='button' onClick={() => setMenuOpen(false)} className={buttonStyles({ variant: 'ghost', size: 'icon' })} aria-label='Tutup menu'>
                        <X className='size-6' />
                    </button>
                </div>

                <nav className='flex-1 overflow-y-auto px-5 py-5' aria-label='Navigasi seluler'>
                    <ul className='space-y-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setMenuOpen(false)}
                                    className={cn('block rounded-2xl px-4 py-3 font-heading text-lg font-semibold text-brand-choco hover:bg-brand-sand/70', isActive(link.href) && 'bg-brand-yellow')}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <p className='mb-2 mt-7 px-1 font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground'>
                        Kategori
                    </p>
                    <ul className='grid grid-cols-2 gap-2'>
                        {categories.map(category => (
                            <li key={category.id}>
                                <Link
                                    href={`/produk?kategori=${category.slug}`}
                                    onClick={() => setMenuOpen(false)}
                                    className='flex items-center gap-2 rounded-2xl border-2 border-brand-choco/10 bg-white p-2.5 text-sm font-semibold text-brand-choco hover:border-brand-choco/40'
                                >
                                    <span className='flex size-8 shrink-0 items-center justify-center rounded-lg border-2 border-brand-choco' style={{ backgroundColor: category.color }}>
                                        <CategoryIcon slug={category.slug} className='size-4' />
                                    </span>
                                    <span className='line-clamp-2 leading-tight' data-mdlfy-cms={`categories:${category.id}:name`}>
                                        {category.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='border-t-2 border-brand-choco/10 p-5'>
                    <Link href='/produk' onClick={() => setMenuOpen(false)} className={buttonStyles({ variant: 'primary', size: 'lg', className: 'w-full' })}>
                        Mulai Belanja
                    </Link>
                </div>
            </Drawer>
        </>
    )
}
